import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { Menu, X } from "lucide-react";

export const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Block body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? "bg-black/60 backdrop-blur-md py-3 border-b border-white/10" : "bg-transparent py-6"}`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {!mobileMenuOpen && (
            <Link to="/" className="flex items-center gap-2 group relative z-[60]">
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center font-bold text-black italic group-hover:scale-110 transition-transform text-lg">S</div>
              <span className="text-xl font-bold tracking-tighter">SetupOnline</span>
            </Link>
          )}

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/solutions" className={`text-sm font-medium transition-colors ${pathname === "/solutions" ? "text-orange-500" : "text-white/70 hover:text-orange-500"}`}>Solutions</Link>
            <Link to="/pricing" className={`text-sm font-medium transition-colors ${pathname === "/pricing" ? "text-orange-500" : "text-white/70 hover:text-orange-500"}`}>Pricing</Link>
            <Link to="/white-label" className={`text-sm font-medium transition-colors ${pathname === "/white-label" ? "text-orange-500" : "text-white/70 hover:text-orange-500"}`}>White-Label</Link>
            <Link to="/blog" className={`text-sm font-medium transition-colors ${pathname === "/blog" ? "text-orange-500" : "text-white/70 hover:text-orange-500"}`}>Blog</Link>
            <Link to="/demo" className={`text-sm font-medium transition-colors ${pathname === "/demo" ? "text-orange-500" : "text-white/70 hover:text-orange-500"}`}>Contact</Link>
            <Link to="/demo" className="px-5 py-2 bg-white text-black rounded-full text-sm font-semibold hover:bg-orange-500 hover:text-white transition-all">Get a Demo</Link>
          </div>

          {/* Mobile Toggle */}
          {!mobileMenuOpen && (
            <button 
              className="md:hidden text-white p-2 relative z-[60]" 
              onClick={() => setMobileMenuOpen(true)} 
              aria-label="Open menu"
            >
              <Menu className="w-8 h-8" />
            </button>
          )}
        </div>
      </nav>

      {/* Mobile Menu Full Screen Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 bg-[#050505] z-[55] flex flex-col pt-8 px-6"
          >
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between mb-16 px-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center font-bold text-black italic text-lg">S</div>
                <span className="text-xl font-bold tracking-tighter">SetupOnline</span>
              </div>
              <button 
                onClick={() => setMobileMenuOpen(false)}
                className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-white"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Background Decor */}
            <div className="absolute top-1/4 right-0 w-[300px] h-[300px] bg-orange-500/10 rounded-full blur-[100px] -z-10" />
            
            <div className="flex flex-col gap-8">
              {[
                { label: "Home", path: "/" },
                { label: "Solutions", path: "/solutions" },
                { label: "Pricing", path: "/pricing" },
                { label: "White-Label", path: "/white-label" },
                { label: "Blog", path: "/blog" },
                { label: "Contact Us", path: "/demo" }
              ].map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.1 }}
                >
                  <Link 
                    to={link.path} 
                    onClick={() => setMobileMenuOpen(false)} 
                    className={`text-3xl sm:text-4xl font-bold tracking-tight ${pathname === link.path ? "text-orange-500" : "text-white"}`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-8"
              >
                <Link 
                  to="/demo" 
                  onClick={() => setMobileMenuOpen(false)} 
                  className="inline-block w-full py-5 bg-orange-500 text-white rounded-2xl text-center font-bold text-xl"
                >
                  Get a Free Demo
                </Link>
              </motion.div>
            </div>
            
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="mt-auto pb-12"
            >
              <div className="flex flex-col gap-4 text-white/30 text-sm font-medium">
                <p>Email: partnership@setuponline.in</p>
                <p>© {new Date().getFullYear()} SetupOnline Global</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
